import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Checkbox, Form, Input, Select } from 'antd'
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  Alert,
  AlertIcon,
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ClickHereMessage,
  DateFormatter,
  type NormalizedInputNumberHandle,
  NormalizedDatePicker,
  NormalizedInputNumber,
  NumberFormatter,
  RequiredTag,
  UnitLabel
} from '@/components/elements'
import { type SingleOnChange } from '@/components/types'
import { EMPTY_LABEL } from '@/constants'
import {
  ACTIONS,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  IMPORTED_SHIPMENT_ORDER_EDIT_NOTICE_PARTS,
  LABELS,
  STATUSES,
  UNITS
} from '@/constants/common/message.constants'
import { AreaSelect } from '@/features/areas/components'
import { useConfig } from '@/features/config/hooks'
import { useCustomKeySettingsQuery } from '@/features/custom-key-settings/hooks'
import { getVisibleCustomKeySlots } from '@/features/custom-key-settings/models'
import { CustomerSelect } from '@/features/customers/components'
import { getCustomerQueryOptions } from '@/features/customers/query-options'
import { PackageCategorySelect } from '@/features/package-categories/components'
import { PartnerSearchSelect } from '@/features/partners/components'
import { getInHouseQueryOptions } from '@/features/partners/query-options'
import { TaxCategorySelect } from '@/features/shared/components'
import {
  calculateIncomeRecordingDate,
  toastApiErrorMessage
} from '@/features/shared/utils'
import {
  ShipmentCategorySelect,
  ShipmentCategoryTag
} from '@/features/shipment-categories/components'
import {
  type CustomerCargoHandlingDefaults,
  useCustomerCargoHandlingDefaults,
  useCustomerDefaultAdditionals
} from '@/features/shipment-orders/hooks'
import { SiteSelectWithLocation } from '@/features/sites/components'
import { useTaxCategoriesSelectQuery } from '@/features/tax-categories/hooks'
import { TruckModelSelect } from '@/features/truck-models/components'
import {
  type AutoCalculateIncomeEntity,
  type SiteSelectListExEntity,
  type UserMeExEntity
} from '@/libs/api'
import { ROUND_TYPE_OPTIONS } from '@/libs/api/types'
import { useFollowDeliveryByShipping } from '@/libs/hooks/use-follow-delivery-by-shipping'
import { safeFormatISO, safeParseISO } from '@/libs/utils'

import { autoCalculateIncome } from '../../api'
import {
  createInitialAdditionalsFromDefaults,
  type ShipmentOrderAdditionalFormValue
} from '../../models'
import {
  type FieldArrayName,
  ShipmentOrderAdditionalsSection
} from '../ShipmentOrderAdditionalsSection'
import { ShipmentOrderCargoHandlingSection } from '../ShipmentOrderCargoHandlingSection'
import { ShipmentOrderWaypointsSection } from '../ShipmentOrderWaypointsSection'

import { HistoryLoadRow } from './HistoryLoadRow'
import { ItemRows } from './ItemRows'
import {
  type ShipmentOrderCardItemFormValue,
  type ShipmentOrderCardNamePrefix
} from './shipment-order-card-form-values'
import { type ShipmentOrderFormCardMode } from './shipment-order-form-card-mode'
import { TimeRestrictionRows } from './TimeRestrictionRows'
import { useShipmentOrderCardField } from './use-shipment-order-card-field'

/**
 * カードが閲覧専用表示・インポート判定の元にする配送依頼の情報。
 * `ShipmentOrdersListExEntity`（未配車編集）と `ShipmentOrderInfo`（矢羽根編集）の
 * 両方が構造的に代入可能な最小限のフィールドだけを持つ。
 */
export type ShipmentOrderCardSourceOrder = {
  isManual: boolean
  /**
   * 矢羽根編集（`ShipmentOrderInfo`）は配送依頼グループが存在しない状態を
   * 表現できるため nullable。読取専用セルは `sourceOrder?.group?.xxx` で耐える。
   */
  group: {
    customer?: { name?: string | null; code?: string | null } | null
    shipmentCategory?: {
      name?: string | null
      displayColor?: string | null
    } | null
    originSite?: {
      name?: string | null
      code?: string | null
      location?: string | null
    } | null
    destSite?: {
      name?: string | null
      code?: string | null
      location?: string | null
    } | null
    packageCategory?: { name?: string | null } | null
    /** 車種セレクトの拠点絞り込みに使う */
    dcId: number
    /** 発日（閲覧専用表示のフォーマット用） */
    shippingDate: string
    /** 着日（閲覧専用表示のフォーマット用） */
    deliveryDate: string
  } | null
  partner?: { id: number; name: string } | null
  truckModel?: { id: number; name: string } | null
  customerContractName?: string | null
  regularGroupId?: string | null
  /** 配送依頼No（閲覧専用表示） */
  orderNo: string
  /** 請求運賃（閲覧専用表示） */
  income: number | null
  /** 貰い運賃の税区分（閲覧専用表示の名称解決に使用） */
  incomeTaxCategoryId?: number | null
  /** 請求計上日（閲覧専用表示） */
  incomeRecordingDate: string
  departureTimeRestrictionStart: string | null
  departureTimeRestrictionEnd: string | null
  arrivalTimeRestrictionStart: string | null
  arrivalTimeRestrictionEnd: string | null
  /** 経由地（閲覧専用表示は件数のみ） */
  waypoints?:
    | { siteId: number; note: string | null; entryTime?: string | null }[]
    | null
  customKey1?: string | null
  customKey2?: string | null
  customKey3?: string | null
  customKey4?: string | null
  customKey5?: string | null
}

export type ShipmentOrderFormCardProps = {
  mode: ShipmentOrderFormCardMode
  tenantKey: string
  authUser: UserMeExEntity
  selectedDcId: number | null
  /** RHF フィールド名の接頭辞。矢羽根編集は `shipments.${index}.` を渡す */
  namePrefix?: ShipmentOrderCardNamePrefix
  setNumericRef: (
    name: string
  ) => (el: NormalizedInputNumberHandle | null) => void
  /** 更新系モードで読取専用セルの表示元・インポート判定元となる元データ */
  sourceOrder?: ShipmentOrderCardSourceOrder
  /** フォーム値に partnerId を持たない経路でパートナー欄を読取専用にする */
  partnerReadOnly?: boolean
  lastEstimatedIncome: AutoCalculateIncomeEntity | undefined
  onEstimatedIncomeChange: (v: AutoCalculateIncomeEntity | undefined) => void
  /** undefined のとき定期運行の行を描画しない */
  /** 着日変更に伴う矢羽根時刻の調整は親が行う */
  onDeliveryDateChange?: (args: {
    prev: Date | string | null
    next: Date | string | null
  }) => void
  /** create モードの履歴読込行 */
  historyLoad?: { onEditRequest: (req: unknown) => void }
  /**
   * インポート配送依頼の「全フィールド編集を許可」状態の変化を親へ通知する。
   * dispatchedUpdate はこの状態と同期させたいカード外項目（矢羽根の時間行）を持つため、
   * カード内 state (allowFullEditForImported) をカード外へ伝える経路として使う。
   */
  onAllowFullEditForImportedChange?: (allowFullEditForImported: boolean) => void
  /**
   * 配車板のモーダル（矢羽根に載る依頼）だけが渡す、地点ごとの時刻欄（#5548）。
   * 発地行に矢羽根の開始時刻、着地行に終了時刻の入力欄を置き、経由地の各行に到着予定時刻の
   * 欄を出す。フォーム値の持ち主は呼び出し側（矢羽根の時刻は root、経由地の時刻はカードの
   * `waypoints.{i}.entryTime`）。未配車の依頼フォームは渡さない
   */
  stopTimeFields?: {
    /** 発地行に出す矢羽根の開始時刻の欄 */
    origin: ReactNode
    /** 着地行に出す矢羽根の終了時刻の欄 */
    dest: ReactNode
    /**
     * 矢羽根の時間帯（発地行・着地行の時間欄の現在値）。経由地の時間のカレンダーで選べる
     * 日をこの範囲に絞る。保存時の検証（矢羽根の時間帯の範囲内）と同じ出どころにする
     */
    range: {
      startAt: Date | string | null | undefined
      endAt: Date | string | null | undefined
    }
  }
}

/** 自動計算ボタンの活性条件（見積結果が未確定、または金額が見積結果から変更されている） */
const isAutoCalcEnabled = (
  isEstimating: boolean,
  lastEstimatedIncome: AutoCalculateIncomeEntity | undefined,
  income: number | null | undefined
) =>
  !isEstimating &&
  (!lastEstimatedIncome || income !== lastEstimatedIncome.income)

/**
 * 配送依頼の「依頼情報＋明細」フィールド群。
 * 配送依頼の編集（未配車）・配送依頼の作成・スケジュール枠＋配送依頼の作成・
 * 配車情報の編集（矢羽根）の4モーダルで共有する。
 *
 * `partnerReadOnly` はフォーム値に partnerId を持たない（矢羽根の編集では指定パートナーは不要）の経路向けの読取専用指定。
 */
export const ShipmentOrderFormCard: FC<ShipmentOrderFormCardProps> = ({
  mode,
  tenantKey,
  authUser,
  selectedDcId,
  namePrefix,
  setNumericRef,
  sourceOrder,
  partnerReadOnly,
  lastEstimatedIncome,
  onEstimatedIncomeChange,
  onDeliveryDateChange,
  historyLoad,
  onAllowFullEditForImportedChange,
  stopTimeFields
}) => {
  const { areas } = useConfig()
  const { name, watchField, setField, form } =
    useShipmentOrderCardField(namePrefix)
  const { control, setValue, getValues, setError, clearErrors, formState } =
    form

  // インポート配送依頼の全フィールド編集を許可するかどうか
  const [allowFullEditForImported, setAllowFullEditForImported] =
    useState(false)

  // allowFullEditForImported はカード内 state のため、カード外の項目
  // （矢羽根編集の TIME 行）と編集可否を同期させたい呼び出し側へ変化を通知する。
  useEffect(() => {
    onAllowFullEditForImportedChange?.(allowFullEditForImported)
    // onAllowFullEditForImportedChange は呼び出し側で毎レンダー新規生成されうるため
    // 依存配列から除外する（allowFullEditForImported の変化のみを通知トリガーにする）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowFullEditForImported])

  // 編集可能かどうかの判定（インポート配送依頼で全フィールド編集を許可した場合も編集可能。
  // create は既存データが無いため常に編集可能）
  const canEditFields =
    mode === 'create' ||
    (sourceOrder?.isManual ?? true) ||
    allowFullEditForImported

  /**
   * 請求計上日を編集できるか。
   * 矢羽根編集ではインポート由来の依頼でも計上日だけは締め処理の都合で常に直せる。
   * 他モードは他の項目と同じくインポート読取専用ゲートに従う。
   */
  const canEditIncomeRecordingDate =
    mode === 'dispatchedUpdate' || canEditFields

  /**
   * 荷主・配送種別の選択をクリア可能にするか。
   * この2つは履歴読込セレクトの絞り込み条件でもあり、create では候補を広げるために
   * 外せる必要がある。更新系は既存の確定値を誤って空にできないよう禁止する。
   * 発着地は絞り込み条件ではないため、必須項目としてどのモードでもクリア不可。
   */
  const canClearHistoryFilterSelects = mode === 'create'

  // create モードで履歴を復元した荷主を記録する ref。荷主 default の自動反映
  // （税カスケード effect・付帯費用投入）を抑止するために使う（#3875 / #4076 相当）。
  const historyRestoredCustomerIdRef = useRef<number | null>(null)
  // 履歴復元のたびに税カスケード effect を再評価させるためのカウンタ（#4268 相当）。
  const [historyRestoreCount, setHistoryRestoreCount] = useState(0)

  const handleHistoryRestored = useCallback((customerId: number | null) => {
    historyRestoredCustomerIdRef.current = customerId
    setHistoryRestoreCount((c) => c + 1)
  }, [])

  const [isEstimating, setIsEstimating] = useState(false)

  const incomeWatchValue = watchField('income') as number | null | undefined
  const isAutoCalcButtonEnabled = isAutoCalcEnabled(
    isEstimating,
    lastEstimatedIncome,
    incomeWatchValue
  )

  const originSiteWatchValue = watchField('originSiteId') as number | null
  const destSiteWatchValue = watchField('destSiteId') as number | null
  const customerIdWatchValue = watchField('customerId') as number | null

  // 荷主を履歴復元時と異なる値に変更したら、復元由来の抑止を解除する（#3875 相当）。
  useEffect(() => {
    if (
      historyRestoredCustomerIdRef.current != null &&
      customerIdWatchValue !== historyRestoredCustomerIdRef.current
    ) {
      historyRestoredCustomerIdRef.current = null
    }
  }, [customerIdWatchValue])

  // create モード専用: 荷主選択時に、その荷主のデフォルト付帯費用カテゴリを
  // 請求側付帯費用へ自動投入する（#1857 指摘-4 相当）。
  const applyCustomerDefaultAdditionals = useCallback(
    (categories: { id: number; isFareIncludedByDefault: boolean }[]) => {
      // 履歴復元由来の荷主は履歴の付帯費用を優先し、荷主 default は投入しない。
      if (
        getValues(name('customerId')) === historyRestoredCustomerIdRef.current
      ) {
        const kept = (
          (getValues(name('additionalCharges')) ??
            []) as ShipmentOrderAdditionalFormValue[]
        ).filter((r) => !r.uniqueId?.startsWith('default_'))
        setField('additionalCharges', kept, {
          shouldDirty: true,
          shouldValidate: true
        })
        return
      }
      // 手動の荷主選択は「追加」でなく「完全な置き換え（リセット）」。
      setField(
        'additionalCharges',
        createInitialAdditionalsFromDefaults(categories),
        { shouldDirty: true, shouldValidate: true }
      )
    },
    // name() は namePrefix から作る非メモ化のクロージャのため依存配列から除外する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, setField]
  )
  useCustomerDefaultAdditionals({
    tenantKey,
    customerId: customerIdWatchValue,
    enabled: mode === 'create',
    onApply: applyCustomerDefaultAdditionals
  })

  // #4741 — 荷主マスタが持つ荷役作業の既定値を初期表示する。
  // 未設定 (null) の項目は投入しない。既定値を持たない荷主へ切り替えたときに、
  // 前の荷主の値をクリアするのではなく手入力を尊重するためである。
  const applyCustomerCargoHandlingDefaults = useCallback(
    (defaults: CustomerCargoHandlingDefaults) => {
      // 履歴復元由来の荷主なら、荷主既定値を投入しない。入力履歴は荷役作業4項目も
      // 復元する (#4812) ため、ここで投入すると復元直後に荷主既定値が復元値を
      // 上書きし、履歴を選んだのに履歴と違う値が入る。付帯費用の既定値
      // (applyCustomerDefaultAdditionals) と同じ抑止である。
      if (
        getValues(name('customerId')) === historyRestoredCustomerIdRef.current
      ) {
        return
      }
      const pairs = [
        {
          outsourcedField: 'isLoadingWorkOutsourced',
          outsourced: defaults.isLoadingWorkOutsourced,
          minutesField: 'loadingWorkMinutes',
          minutes: defaults.loadingWorkMinutes,
          noteField: 'loadingWorkNote'
        },
        {
          outsourcedField: 'isUnloadingWorkOutsourced',
          outsourced: defaults.isUnloadingWorkOutsourced,
          minutesField: 'unloadingWorkMinutes',
          minutes: defaults.unloadingWorkMinutes,
          noteField: 'unloadingWorkNote'
        }
      ] as const
      for (const {
        outsourcedField,
        outsourced,
        minutesField,
        minutes,
        noteField
      } of pairs) {
        if (outsourced != null) {
          setField(outsourcedField, outsourced, {
            shouldDirty: true,
            shouldValidate: true
          })
        }
        if (outsourced === false) {
          // 委託なしの既定値を持つ荷主へ切り替えたときは、委託ありのときだけ出す
          // 項目（予定作業時間・備考）も消す。チェックが外れて入力欄はアンマウント
          // されるが RHF の値は残るため、消さないと「委託なしなのに 30 分」が
          // そのまま保存され、帳票 (#4751) が「無」と「30分」を同じ行に印字する。
          // 既定値の投入はユーザー操作を経ないので、チェックボックスの onChange 側の
          // クリアでは拾えない。
          //
          // 備考は荷主マスタの既定値を持たない (#4741)。委託なしなら消すだけである。
          for (const field of [minutesField, noteField] as const) {
            setField(field, null, { shouldDirty: true, shouldValidate: true })
          }
          continue
        }
        if (minutes != null) {
          setField(minutesField, minutes, {
            shouldDirty: true,
            shouldValidate: true
          })
        }
      }
    },
    // name() は namePrefix から作る非メモ化のクロージャのため依存配列から除外する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, setField]
  )
  useCustomerCargoHandlingDefaults({
    tenantKey,
    customerId: customerIdWatchValue,
    enabled: mode === 'create',
    onApply: applyCustomerCargoHandlingDefaults
  })

  const shippingDateWatchValue = watchField('shippingDate') as
    | Date
    | string
    | null
  const deliveryDateWatchValue = watchField('deliveryDate') as
    | Date
    | string
    | null

  // 自動計算の対象重量算出に使う（明細の管理自体は ItemRows が持つ）
  const rawWatchedItems = watchField('items') as
    | ShipmentOrderCardItemFormValue[]
    | undefined
  const watchedItems = useMemo(() => rawWatchedItems ?? [], [rawWatchedItems])

  // #4733: エリアはフォーム値。以前はローカル state だったため保存されず、
  // 編集モーダルを開き直すと選択が消えていた
  const originSelectedAreaId = (watchField('originAreaId') ?? null) as
    | number
    | null
  const destSelectedAreaId = (watchField('destAreaId') ?? null) as number | null
  const setOriginSelectedAreaId = (value: number | null): void =>
    setField('originAreaId', value, { shouldDirty: true })
  const setDestSelectedAreaId = (value: number | null): void =>
    setField('destAreaId', value, { shouldDirty: true })

  // 着日が発送日に追従するhook
  useFollowDeliveryByShipping({
    control,
    setValue,
    shippingName: name('shippingDate'),
    deliveryName: name('deliveryDate')
  })

  // 自動計算ボタンの活性条件に連動して、請求運賃入力の背景色をハイライト
  // bg-yellow-50 相当 (#FFFBEB)。見やすいように枠線も薄い黄色に。
  const incomeHighlightStyle = isAutoCalcButtonEnabled
    ? { backgroundColor: '#FFFBEB', borderColor: '#FDE68A' }
    : undefined

  // #3463 VOC_PJ-829/851: 拠点のカスタムキーから isVisible 指定分のみ動的に入力欄を表示する
  const { data: customKeySettings } = useCustomKeySettingsQuery(
    tenantKey,
    selectedDcId
  )
  const visibleCustomKeySlots = useMemo(
    () => getVisibleCustomKeySlots(customKeySettings),
    [customKeySettings]
  )

  // VOC_PJ-55 #1846 4589168986: 編集時のカスケード再計算
  // ユーザが「荷主」または「発送日 / 着日」を変更した時点で
  // 請求計上日をマスタの設定値（荷主 → 拠点 IN_HOUSE）に基づいて再計算する
  const customerQuery = useQuery({
    ...getCustomerQueryOptions({
      tenantKey,
      customerId: customerIdWatchValue!
    }),
    enabled: customerIdWatchValue != null && customerIdWatchValue > 0
  })
  const inHouseQuery = useQuery({
    ...getInHouseQueryOptions({
      tenantKey,
      dcId: selectedDcId!
    }),
    enabled: selectedDcId != null && selectedDcId > 0
  })

  // getFieldState は namePrefix 込みのパスにも対応するため、dirtyFields の直接参照より汎用
  const isCustomerIdDirty = form.getFieldState(
    name('customerId'),
    formState
  ).isDirty
  const isShippingDateDirty = form.getFieldState(
    name('shippingDate'),
    formState
  ).isDirty
  const isDeliveryDateDirty = form.getFieldState(
    name('deliveryDate'),
    formState
  ).isDirty

  useEffect(() => {
    // ユーザが触っていなければ再計算しない（DB 既存値を維持）
    if (!isCustomerIdDirty && !isShippingDateDirty && !isDeliveryDateDirty) {
      return
    }
    const customer = customerQuery.data
    const inHousePartner = inHouseQuery.data
    const shipping = safeParseISO(shippingDateWatchValue)
    const delivery = safeParseISO(deliveryDateWatchValue)
    if (!customer || !inHousePartner || !shipping || !delivery) return

    const calculated = calculateIncomeRecordingDate({
      customer: { incomeRecordingDateType: customer.incomeRecordingDateType },
      inHousePartner: {
        defaultIncomeRecordingDateType:
          inHousePartner.defaultIncomeRecordingDateType
      },
      group: { shippingDate: shipping, deliveryDate: delivery }
    })

    const current = safeParseISO(getValues(name('incomeRecordingDate')))
    if (current && current.getTime() === calculated.getTime()) return

    setValue(name('incomeRecordingDate'), calculated.toISOString(), {
      shouldValidate: true,
      shouldDirty: true
    })
    // name() は非メモ化のクロージャのため依存配列から除外する（上の注記と同じ理由）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCustomerIdDirty,
    isShippingDateDirty,
    isDeliveryDateDirty,
    customerQuery.data,
    inHouseQuery.data,
    shippingDateWatchValue,
    deliveryDateWatchValue,
    getValues,
    setValue
  ])

  // #3328 VOC_PJ-761 / #3875: 荷主を変更したときのみ、貰い運賃の税区分・内税を
  // カスケード（荷主 default → 拠点(IN_HOUSE) default → null）で再反映する。
  // 編集時の初期表示は DB 既存値を維持するため、未変更時は何もしない。
  // create は「初期状態から pre-fill する」別の effect（下記）に任せるため対象外にする。
  useEffect(() => {
    if (mode === 'create' || !isCustomerIdDirty) return
    const customer = customerQuery.data
    if (!customer) return
    const inHouse = inHouseQuery.data
    setValue(
      name('incomeTaxCategoryId'),
      customer.defaultTaxCategoryId ?? inHouse?.defaultTaxCategoryId ?? null,
      {
        shouldValidate: true,
        shouldDirty: true
      }
    )
    const nextIsTaxInclusive =
      customer.defaultIsTaxInclusive ?? inHouse?.defaultIsTaxInclusive ?? null
    if (getValues(name('incomeIsTaxInclusive')) !== nextIsTaxInclusive) {
      setValue(name('incomeIsTaxInclusive'), nextIsTaxInclusive, {
        shouldValidate: true,
        shouldDirty: true
      })
    }
    // name() は非メモ化のクロージャのため依存配列から除外する（上の注記と同じ理由）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    isCustomerIdDirty,
    customerQuery.data,
    inHouseQuery.data,
    getValues,
    setValue
  ])

  // create モード専用: 荷主選択時に貰い運賃の税区分・内税・端数処理を
  // カスケード（荷主 default → 拠点(IN_HOUSE) default → null）で pre-fill する。
  // 初期状態からの pre-fill が目的のため、上の update 用 effect（未変更時は何もしない）とは別に持つ。
  useEffect(() => {
    if (mode !== 'create') return
    const customer = customerQuery.data
    if (!customer) return
    if (customer.id !== getValues(name('customerId'))) return
    const isHistoryRestored =
      historyRestoredCustomerIdRef.current === customer.id
    const inHouse = inHouseQuery.data

    const nextTaxCategory =
      customer.defaultTaxCategoryId ?? inHouse?.defaultTaxCategoryId ?? null
    if (
      (!isHistoryRestored || getValues(name('incomeTaxCategoryId')) == null) &&
      getValues(name('incomeTaxCategoryId')) !== nextTaxCategory
    ) {
      setValue(name('incomeTaxCategoryId'), nextTaxCategory, {
        shouldValidate: true,
        shouldDirty: true
      })
    }

    const nextIsTaxInclusive =
      customer.defaultIsTaxInclusive ?? inHouse?.defaultIsTaxInclusive ?? null
    if (
      (!isHistoryRestored || getValues(name('incomeIsTaxInclusive')) == null) &&
      getValues(name('incomeIsTaxInclusive')) !== nextIsTaxInclusive
    ) {
      setValue(name('incomeIsTaxInclusive'), nextIsTaxInclusive, {
        shouldValidate: true,
        shouldDirty: true
      })
    }

    const nextRoundType =
      customer.defaultRoundType ?? inHouse?.defaultRoundType ?? null
    if (
      (!isHistoryRestored || getValues(name('incomeRoundType')) == null) &&
      getValues(name('incomeRoundType')) !== nextRoundType
    ) {
      setValue(name('incomeRoundType'), nextRoundType, {
        shouldValidate: true,
        shouldDirty: true
      })
    }
    // name() は非メモ化のクロージャのため依存配列から除外する（上の注記と同じ理由）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    customerQuery.data,
    inHouseQuery.data,
    getValues,
    setValue,
    historyRestoreCount
  ])

  // create モード専用: 請求計上日の初期値を必ずセットする（発送日 fallback）。
  // 荷主 + 拠点 IN_HOUSE + 着日が揃えば calculateIncomeRecordingDate の 3 段カスケード、
  // 揃っていなければ発送日 (shippingDate) を fallback として常にセットする
  // (請求計上日は必須項目のため、未選択時でも空にしないという業務要件)。
  // 上の update 用 effect（dirty ガード付き・DB 既存値維持）とは目的が異なるため別に持つ。
  useEffect(() => {
    if (mode !== 'create') return
    const customer = customerQuery.data
    const inHousePartner = inHouseQuery.data
    const shipping = safeParseISO(shippingDateWatchValue)
    const delivery = safeParseISO(deliveryDateWatchValue)
    if (!shipping) return

    const calculated =
      customer && inHousePartner && delivery
        ? calculateIncomeRecordingDate({
            customer: {
              incomeRecordingDateType: customer.incomeRecordingDateType
            },
            inHousePartner: {
              defaultIncomeRecordingDateType:
                inHousePartner.defaultIncomeRecordingDateType
            },
            group: { shippingDate: shipping, deliveryDate: delivery }
          })
        : shipping

    const current = safeParseISO(getValues(name('incomeRecordingDate')))
    if (current && current.getTime() === calculated.getTime()) return

    setValue(name('incomeRecordingDate'), calculated.toISOString(), {
      shouldValidate: true,
      shouldDirty: true
    })
    // name() は非メモ化のクロージャのため依存配列から除外する（上の注記と同じ理由）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    customerQuery.data,
    inHouseQuery.data,
    shippingDateWatchValue,
    deliveryDateWatchValue,
    getValues,
    setValue
  ])

  // create モード専用: 拠点 (IN_HOUSE Partner) の税 default を貰い運賃の初期値に適用する。
  // 荷主 default（上の create 用カスケード effect）を優先しつつ、未設定 (null) の項目だけ
  // 拠点 default で補完する。初回 1 回だけ・現在値が null のときのみ反映する
  // (手入力・荷主 default を上書きしない)。
  const appliedInHouseTaxDefaultsRef = useRef(false)
  useEffect(() => {
    if (mode !== 'create') return
    if (appliedInHouseTaxDefaultsRef.current) return
    const p = inHouseQuery.data
    if (!p) return
    appliedInHouseTaxDefaultsRef.current = true
    if (
      getValues(name('incomeTaxCategoryId')) == null &&
      p.defaultTaxCategoryId != null
    ) {
      setValue(name('incomeTaxCategoryId'), p.defaultTaxCategoryId, {
        shouldValidate: true,
        shouldDirty: false
      })
    }
    if (
      getValues(name('incomeIsTaxInclusive')) == null &&
      p.defaultIsTaxInclusive != null
    ) {
      setValue(name('incomeIsTaxInclusive'), p.defaultIsTaxInclusive, {
        shouldValidate: true,
        shouldDirty: false
      })
    }
    if (
      getValues(name('incomeRoundType')) == null &&
      p.defaultRoundType != null
    ) {
      setValue(name('incomeRoundType'), p.defaultRoundType, {
        shouldValidate: true,
        shouldDirty: false
      })
    }
    // name() は非メモ化のクロージャのため依存配列から除外する（上の注記と同じ理由）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, inHouseQuery.data, getValues, setValue])

  // #3328 VOC_PJ-761: 閲覧モードの税区分名表示用（論理削除済も解決できるよう全件取得）
  const { data: taxCategories } = useTaxCategoriesSelectQuery(tenantKey, {
    includeInactive: true
  })
  const incomeTaxCategoryName = useMemo(
    () =>
      taxCategories?.find((tc) => tc.id === sourceOrder?.incomeTaxCategoryId)
        ?.name,
    [taxCategories, sourceOrder?.incomeTaxCategoryId]
  )

  const handleAutoCalculate = useCallback(async () => {
    const customerId = getValues(name('customerId')) as number | null
    const shipmentCategoryId = getValues(name('shipmentCategoryId')) as
      | number
      | null
    const shippingDate = getValues(name('shippingDate')) as Date | string | null
    const originSiteId = getValues(name('originSiteId')) as number | null
    const destSiteId = getValues(name('destSiteId')) as number | null
    const truckModelId = getValues(name('truckModelId')) as number | null

    if (!shipmentCategoryId) {
      toast.error(DESCRIPTIONS.INPUT_SHIPMENT_CATEGORY)
      return
    }
    if (!customerId) {
      toast.error(DESCRIPTIONS.INPUT_CUSTOMER)
      return
    }
    if (!shippingDate) {
      toast.error(DESCRIPTIONS.INPUT_SHIPPING_DATE)
      return
    }
    if (!originSiteId || !destSiteId) {
      toast.error(DESCRIPTIONS.INPUT_ORIGIN_SITE_DEST_SITE)
      return
    }

    const totalWeight = watchedItems.reduce<number | null>(
      (sum, { weight }) => {
        if (weight === null && sum === null) return null

        return (sum ?? 0) + (weight ?? 0)
      },
      null
    )
    if (!truckModelId && totalWeight === null) {
      toast.error(DESCRIPTIONS.INPUT_TRUCK_MODEL_OR_TOTAL_WEIGHT)
      return
    }

    // 実行中はボタン/請求運賃を非活性
    setIsEstimating(true)
    try {
      const autoCalculatedIncome = await autoCalculateIncome({
        tenantKey,
        dto: {
          // selectedDcId は notDispatchedUpdate では常に atom 由来の number（既定 0）。
          // props 契約上は他モード向けに number | null を許容するため、その場合のみ 0 に落とす。
          dcId: selectedDcId ?? 0,
          shippingDate: safeFormatISO(shippingDate),
          customerId,
          shipmentCategoryId,
          originSiteId,
          destSiteId,
          truckModelId,
          totalWeight
        }
      })
      onEstimatedIncomeChange(autoCalculatedIncome ?? undefined)
      if (autoCalculatedIncome.customerContractId === null) {
        toast.error(ERROR_MESSAGES.ESTIMATED_CHARGE_INCOME_NOT_FOUND)
        setValue(name('income'), null, {
          shouldValidate: true,
          shouldDirty: true
        })
        return
      }
      setValue(name('income'), autoCalculatedIncome.income, {
        shouldValidate: true,
        shouldDirty: true
      })
    } catch (error) {
      toastApiErrorMessage(
        error,
        ERROR_MESSAGES.FAILED_TO_CALCULATED_ESTIMATED_CHARGE
      )
    } finally {
      setIsEstimating(false)
    }
  }, [
    getValues,
    name,
    watchedItems,
    tenantKey,
    selectedDcId,
    setValue,
    onEstimatedIncomeChange
  ])

  // --- 自動計算に必要な値 変更時のリセット処理 ---
  const handleAutoCalculateIncomeValueChange = useCallback(() => {
    // 既存の契約結果は無効化（編集中表示へ）
    onEstimatedIncomeChange(undefined)
  }, [onEstimatedIncomeChange])

  /**
   * 地点選択時、対応する備考が空のときだけ地点マスタの `Site.note` をデフォルト補完する。
   * 入力済みの備考は上書きしない（人が書いた備考を潰さない）。
   * `SiteSelectWithLocation` はエリアも選択肢に含むため、エリアを選んだ場合は
   * エリアの備考が入る。
   */
  const copySiteNoteIfEmpty = useCallback(
    (
      noteFieldKey: 'originNote' | 'destNote',
      siteId: number | null,
      option: SiteSelectListExEntity | null
    ) => {
      if (siteId == null) return
      if (getValues(name(noteFieldKey))) return
      if (!option?.note) return
      setField(noteFieldKey, option.note, { shouldDirty: true })
    },
    [getValues, name, setField]
  )

  // --- 契約名セル(リンク)のレンダリング ---
  const renderContractNameCell = useCallback(() => {
    // 契約紐付けの有無だけで判定する（手入力で値≠見積でも契約があれば契約名を出す）。
    // 旧 isAutoCalcButtonEnabled(=ボタン活性) は表示条件から除外した（#3426）。
    const id = lastEstimatedIncome?.customerContractId ?? null

    if (!id) {
      return (
        <AlertIcon severity="error">
          {DESCRIPTIONS.CONTRACT_NOT_FOUND}
        </AlertIcon>
      )
    }

    // 契約名が空文字の場合でもハイフン表示
    const contractName = lastEstimatedIncome?.customerContractName || '-'

    return (
      <Link
        to={'/$tenantKey/settings/customer-contracts/$customerContractId'}
        params={{ tenantKey, customerContractId: id }}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500"
      >
        {contractName}
      </Link>
    )
  }, [lastEstimatedIncome, tenantKey])

  return (
    <>
      {sourceOrder?.regularGroupId && (
        <Alert
          color="default"
          title={DESCRIPTIONS.OTHER_SHIPMENT_ORDERS_NOT_AFFECTED}
          className="mb-4"
        >
          {null}
        </Alert>
      )}
      {/* インポート読み取り専用セクションは notDispatchedUpdate 固有。create は既存データが無いため出さない */}
      {mode !== 'create' &&
        sourceOrder &&
        !sourceOrder.isManual &&
        !allowFullEditForImported && (
          <Alert color="red" className="mb-4" title="">
            <ClickHereMessage
              prefix={IMPORTED_SHIPMENT_ORDER_EDIT_NOTICE_PARTS.prefix}
              suffix={IMPORTED_SHIPMENT_ORDER_EDIT_NOTICE_PARTS.suffix}
              onClick={() => setAllowFullEditForImported(true)}
            />
          </Alert>
        )}
      <div className="relative">
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[150px]" />
            <col />
            <col className="w-[150px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh className="w-[150px] align-top">
                {LABELS.ORDER_NO}
                {/* 新規作成は採番前のため空を許し、編集は既存値があるため必須 */}
                {mode !== 'create' && <RequiredTag className="ml-2" />}
              </BasicTh>
              <BasicTd className="align-top">
                {canEditFields ? (
                  <Controller
                    name={name('orderNo')}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <Input id={field.name} {...field} />
                      </Form.Item>
                    )}
                  />
                ) : (
                  sourceOrder?.orderNo
                )}
              </BasicTd>
              <BasicTh className="align-top">
                {LABELS.SHIPMENT_CATEGORY}
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd className="align-top">
                {canEditFields ? (
                  <Controller
                    control={control}
                    name={name('shipmentCategoryId')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="mb-2"
                      >
                        <ShipmentCategorySelect
                          {...field}
                          id={field.name}
                          tenantKey={tenantKey}
                          value={field.value}
                          allowClear={canClearHistoryFilterSelects}
                          onChange={(value) => {
                            field.onChange(value)
                            handleAutoCalculateIncomeValueChange()
                          }}
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  <ShipmentCategoryTag
                    shipmentCategory={{
                      name: sourceOrder?.group?.shipmentCategory?.name ?? '',
                      displayColor:
                        sourceOrder?.group?.shipmentCategory?.displayColor ?? ''
                    }}
                    width="fit-content"
                  />
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">
                {LABELS.CUSTOMER}
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd className="align-top" colSpan={3}>
                {canEditFields ? (
                  <Controller
                    control={control}
                    name={name('customerId')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <CustomerSelect
                          {...field}
                          authUser={authUser}
                          id={field.name}
                          tenantKey={tenantKey}
                          showSearch
                          value={field.value}
                          allowClear={canClearHistoryFilterSelects}
                          onChange={(value) => {
                            field.onChange(value)
                            handleAutoCalculateIncomeValueChange()
                          }}
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  <p>
                    <span className="font-bold">
                      {sourceOrder?.group?.customer?.name}
                    </span>
                    {sourceOrder?.group?.customer?.code && (
                      <span>({sourceOrder.group?.customer?.code})</span>
                    )}
                  </p>
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh>{LABELS.DESIGNATED_PARTNER}</BasicTh>
              <BasicTd colSpan={3}>
                {!partnerReadOnly && canEditFields ? (
                  <Controller
                    name={name('partnerId')}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        validateStatus={error ? 'error' : undefined}
                        htmlFor={field.name}
                        help={error?.message}
                        className="m-0"
                      >
                        <PartnerSearchSelect
                          {...field}
                          id={field.name}
                          tenantKey={tenantKey}
                          showSearch
                          value={field.value}
                          allowClear
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  (sourceOrder?.partner?.name ?? '-')
                )}
              </BasicTd>
            </BasicTr>

            {/* create モード専用: 履歴読込行 */}
            {mode === 'create' && historyLoad && (
              <HistoryLoadRow
                namePrefix={namePrefix}
                tenantKey={tenantKey}
                onEditRequest={historyLoad.onEditRequest}
                onHistoryRestored={handleHistoryRestored}
              />
            )}

            {/* 発送日・着日は発地・着地の上に置く。矢羽根の時間欄の日付はこの 2 つに固定されるため、
                日を決めてから地点と時刻を決める順に上から読めるようにする（PR #5549 のレビュー指摘） */}
            <BasicTr>
              <BasicTh className="align-top">
                {LABELS.SHIPPING_DATE}
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd className="align-top">
                {canEditFields ? (
                  <Controller
                    control={control}
                    name={name('shippingDate')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <NormalizedDatePicker
                          id={field.name}
                          allowClear={false}
                          placeholder={STATUSES.UNSELECTED}
                          value={safeParseISO(field.value)}
                          onInvalidText={(msg) => {
                            setValue(name('shippingDate'), '', {
                              shouldValidate: false,
                              shouldDirty: true
                            })
                            setError(
                              name('shippingDate'),
                              { type: 'manual', message: msg },
                              { shouldFocus: true }
                            )
                          }}
                          onValidText={() => clearErrors(name('shippingDate'))}
                          onChange={(date) => {
                            field.onChange(date)
                            handleAutoCalculateIncomeValueChange()
                          }}
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  <DateFormatter
                    value={sourceOrder?.group?.shippingDate}
                    dateFormat="yyyy/MM/dd"
                  />
                )}
              </BasicTd>
              <BasicTh className="align-top">
                {LABELS.DELIVERY_DATE}
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd className="align-top">
                {canEditFields ? (
                  <Controller
                    control={control}
                    name={name('deliveryDate')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <NormalizedDatePicker
                          id={field.name}
                          allowClear={false}
                          placeholder={STATUSES.UNSELECTED}
                          value={safeParseISO(field.value)}
                          minDate={
                            shippingDateWatchValue
                              ? new Date(shippingDateWatchValue)
                              : undefined
                          }
                          onInvalidText={(msg) => {
                            setValue(name('deliveryDate'), '', {
                              shouldValidate: false,
                              shouldDirty: true
                            })
                            setError(
                              name('deliveryDate'),
                              { type: 'manual', message: msg },
                              { shouldFocus: true }
                            )
                          }}
                          onValidText={() => clearErrors(name('deliveryDate'))}
                          onChange={(date) => {
                            const prevDeliveryDate = field.value
                            field.onChange(date)
                            onDeliveryDateChange?.({
                              prev: prevDeliveryDate,
                              next: date
                            })
                          }}
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  <DateFormatter
                    value={sourceOrder?.group?.deliveryDate}
                    dateFormat="yyyy/MM/dd"
                  />
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">{LABELS.ORIGIN}</BasicTh>
              <BasicTd className="align-top" colSpan={3}>
                {canEditFields ? (
                  <Controller
                    control={control}
                    name={name('originSiteId')}
                    render={({ field, fieldState: { error } }) => {
                      const handleSiteChange: SingleOnChange<
                        number,
                        SiteSelectListExEntity
                      > = (siteId, option) => {
                        field.onChange(siteId)
                        copySiteNoteIfEmpty('originNote', siteId, option)
                        handleAutoCalculateIncomeValueChange()
                      }
                      return (
                        <Form.Item
                          htmlFor={field.name}
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <div className="mb-3 flex items-center">
                            <span className="w-20 shrink-0">{LABELS.AREA}</span>
                            <AreaSelect
                              showSearch
                              areas={areas}
                              mode={undefined}
                              value={originSelectedAreaId}
                              placeholder={STATUSES.UNSELECTED}
                              popupMatchSelectWidth={false}
                              allowClear
                              className="w-full"
                              onChange={(value) => {
                                if (value !== originSelectedAreaId) {
                                  setValue(
                                    name('originSiteId'),
                                    null as unknown as number
                                  )
                                }
                                setOriginSelectedAreaId(value)
                                handleAutoCalculateIncomeValueChange()
                              }}
                            />
                          </div>
                          {/* 必須なのは地点だけなので、必須タグは行見出しではなく地点のラベルに付ける */}
                          <div className="flex items-center">
                            <label
                              htmlFor={field.name}
                              className="w-20 shrink-0"
                            >
                              {LABELS.STOP_SITE}
                              <RequiredTag className="ml-1" />
                            </label>
                            <div className="min-w-0 flex-1">
                              <SiteSelectWithLocation
                                {...field}
                                id={field.name}
                                tenantKey={tenantKey}
                                showSearch
                                placeholder={STATUSES.UNSELECTED}
                                disabledIds={
                                  originSiteWatchValue
                                    ? [originSiteWatchValue]
                                    : []
                                }
                                value={field.value}
                                allowClear={false}
                                onChange={handleSiteChange}
                                selectedAreaId={originSelectedAreaId}
                                customerId={customerIdWatchValue}
                                enableCustomerLink
                              />
                            </div>
                          </div>
                        </Form.Item>
                      )
                    }}
                  />
                ) : (
                  <>
                    <p>
                      <span className="font-bold">
                        {sourceOrder?.group?.originSite?.name}
                      </span>
                      <span>({sourceOrder?.group?.originSite?.code})</span>
                    </p>
                    <p>{sourceOrder?.group?.originSite?.location}</p>
                  </>
                )}
                {/*
                  発地備考。地点マスタの備考を初期値に取りつつ依頼ごとに直せる欄で、
                  配車板ホバーの配送ルート表示にそのまま出る。
                  地点とセットで見る項目のため発地の行内（地点プルダウンの下）に置く。
                  `note`（メモ）と同じくインポート由来の依頼でも編集させる
                  （運用メモであり、インポートで運ばれる「公式データ」ではない）ため、
                  canEditFields の分岐の外に出す。
                */}
                <div className="mt-3 flex items-center">
                  <span className="w-20 shrink-0">{LABELS.NOTE}</span>
                  <Controller
                    control={control}
                    name={name('originNote')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0 flex-1"
                      >
                        <Input
                          id={field.name}
                          {...field}
                          value={field.value ?? ''}
                          allowClear
                        />
                      </Form.Item>
                    )}
                  />
                </div>
                {/* 矢羽根の開始時刻（#5548）。配車板のモーダルだけが渡す。地点とセットで見る
                    項目なので発地の行内（備考の下）に置く */}
                {stopTimeFields && (
                  <div className="mt-3 flex items-center">
                    <span className="w-20 shrink-0">{LABELS.TIME}</span>
                    {stopTimeFields.origin}
                  </div>
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">{LABELS.WAYPOINT}</BasicTh>
              <BasicTd className="align-top" colSpan={3}>
                {canEditFields ? (
                  <ShipmentOrderWaypointsSection
                    tenantKey={tenantKey}
                    customerId={customerIdWatchValue}
                    namePrefix={namePrefix}
                    showEntryTime={stopTimeFields != null}
                    entryTimeRange={stopTimeFields?.range}
                  />
                ) : (sourceOrder?.waypoints ?? []).length > 0 ? (
                  <p>
                    {(sourceOrder?.waypoints ?? []).length}
                    {LABELS.COUNT_UNIT}
                  </p>
                ) : (
                  <p>-</p>
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">{LABELS.DESTINATION}</BasicTh>
              <BasicTd className="align-top" colSpan={3}>
                {canEditFields ? (
                  <Controller
                    control={control}
                    name={name('destSiteId')}
                    render={({ field, fieldState: { error } }) => {
                      const handleSiteChange: SingleOnChange<
                        number,
                        SiteSelectListExEntity
                      > = (siteId, option) => {
                        field.onChange(siteId)
                        copySiteNoteIfEmpty('destNote', siteId, option)
                        handleAutoCalculateIncomeValueChange()
                      }
                      return (
                        <Form.Item
                          htmlFor={field.name}
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <div className="mb-3 flex items-center">
                            <span className="w-20 shrink-0">{LABELS.AREA}</span>
                            <AreaSelect
                              showSearch
                              areas={areas}
                              mode={undefined}
                              value={destSelectedAreaId}
                              placeholder={STATUSES.UNSELECTED}
                              popupMatchSelectWidth={false}
                              allowClear
                              className="w-full"
                              onChange={(value) => {
                                if (value !== destSelectedAreaId) {
                                  setValue(
                                    name('destSiteId'),
                                    null as unknown as number
                                  )
                                }
                                setDestSelectedAreaId(value)
                                handleAutoCalculateIncomeValueChange()
                              }}
                            />
                          </div>
                          {/* 必須なのは地点だけなので、必須タグは行見出しではなく地点のラベルに付ける */}
                          <div className="flex items-center">
                            <label
                              htmlFor={field.name}
                              className="w-20 shrink-0"
                            >
                              {LABELS.STOP_SITE}
                              <RequiredTag className="ml-1" />
                            </label>
                            <div className="min-w-0 flex-1">
                              <SiteSelectWithLocation
                                {...field}
                                id={field.name}
                                tenantKey={tenantKey}
                                showSearch
                                placeholder={STATUSES.UNSELECTED}
                                disabledIds={
                                  destSiteWatchValue ? [destSiteWatchValue] : []
                                }
                                value={field.value}
                                allowClear={false}
                                onChange={handleSiteChange}
                                selectedAreaId={destSelectedAreaId}
                                customerId={customerIdWatchValue}
                                enableCustomerLink
                              />
                            </div>
                          </div>
                        </Form.Item>
                      )
                    }}
                  />
                ) : (
                  <>
                    <p>
                      <span className="font-bold">
                        {sourceOrder?.group?.destSite?.name}
                      </span>
                      <span>({sourceOrder?.group?.destSite?.code})</span>
                    </p>
                    <p>{sourceOrder?.group?.destSite?.location}</p>
                  </>
                )}
                {/* 着地備考。発地備考と同じ扱い（着地の行内・インポート由来でも編集可） */}
                <div className="mt-3 flex items-center">
                  <span className="w-20 shrink-0">{LABELS.NOTE}</span>
                  <Controller
                    control={control}
                    name={name('destNote')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0 flex-1"
                      >
                        <Input
                          id={field.name}
                          {...field}
                          value={field.value ?? ''}
                          allowClear
                        />
                      </Form.Item>
                    )}
                  />
                </div>
                {/* 矢羽根の終了時刻（#5548）。発地行の開始時刻と対になる */}
                {stopTimeFields && (
                  <div className="mt-3 flex items-center">
                    <span className="w-20 shrink-0">{LABELS.TIME}</span>
                    {stopTimeFields.dest}
                  </div>
                )}
              </BasicTd>
            </BasicTr>

            <TimeRestrictionRows
              namePrefix={namePrefix}
              canEditFields={canEditFields}
              sourceOrder={
                sourceOrder && {
                  departureTimeRestrictionStart:
                    sourceOrder.departureTimeRestrictionStart,
                  departureTimeRestrictionEnd:
                    sourceOrder.departureTimeRestrictionEnd,
                  arrivalTimeRestrictionStart:
                    sourceOrder.arrivalTimeRestrictionStart,
                  arrivalTimeRestrictionEnd:
                    sourceOrder.arrivalTimeRestrictionEnd
                }
              }
            />

            <ShipmentOrderCargoHandlingSection
              control={control}
              loadingOutsourcedName={name('isLoadingWorkOutsourced')}
              loadingMinutesName={name('loadingWorkMinutes')}
              loadingNoteName={name('loadingWorkNote')}
              unloadingOutsourcedName={name('isUnloadingWorkOutsourced')}
              unloadingMinutesName={name('unloadingWorkMinutes')}
              unloadingNoteName={name('unloadingWorkNote')}
              isEditing={canEditFields}
              setError={setError}
              clearErrors={clearErrors}
              setValue={setValue}
            />

            <BasicTr>
              <BasicTh className="w-[150px] align-top">{LABELS.NOTE}</BasicTh>
              <BasicTd className="align-top" colSpan={3}>
                <Controller
                  name={name('note')}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input.TextArea
                        autoSize={{ minRows: 3 }}
                        id={field.name}
                        {...field}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>

            {/*
              #3463 VOC_PJ-829/851: 拠点のカスタムキーで isVisible 指定されたスロットのみ
              入力欄を動的に表示する（固定5項目を常に表示しない）。
              実運用ではCSVインポートが主要導線のため、他の「公式データ」項目と同様に
              canEditFields（インポート済みは「全項目編集を許可」クリックが必要）に従わせる。
            */}
            {visibleCustomKeySlots.map(({ fieldName, label }) => (
              <BasicTr key={fieldName}>
                <BasicTh className="w-[150px] align-top">{label}</BasicTh>
                <BasicTd className="align-top" colSpan={3}>
                  {canEditFields ? (
                    <Controller
                      name={name(fieldName)}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <Input
                            id={field.name}
                            {...field}
                            value={field.value ?? ''}
                            maxLength={30}
                          />
                        </Form.Item>
                      )}
                    />
                  ) : (
                    (sourceOrder?.[fieldName] ?? EMPTY_LABEL)
                  )}
                </BasicTd>
              </BasicTr>
            ))}

            {/*
              VOC_PJ-844: 請求運賃を「金額・税区分・内税・自動計算・契約名」の横並びに統合
              (配車情報編集モーダルの払い運賃 / 配車板の請求運賃と統一)。
              金額は ¥ 付き・左寄せ (w-[110px])、税区分も付帯運賃の税区分プルダウンと
              同幅 (w-[110px])。自動計算の右に契約名/契約未検出を表示。
              付帯費用との縦間隔は同カテゴリのため詰める (!pb-2 + セクション !pt-0/!mt-0)。
            */}
            <BasicTr className="border-b-0">
              <BasicTh className="align-top">
                {LABELS.INCOME_FARE}
                <UnitLabel>{UNITS.CURRENCY.YEN_PARENTHESES}</UnitLabel>
              </BasicTh>
              <BasicTd className="!pb-2 align-top" colSpan={3}>
                {canEditFields ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {/* 金額 */}
                    <Controller
                      name={name('income')}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <NormalizedInputNumber
                            ref={setNumericRef(name('income'))}
                            id={field.name}
                            value={field.value}
                            onChange={(val) => field.onChange(val)}
                            onInvalidValue={(msg) =>
                              setError(
                                name('income'),
                                {
                                  message: msg
                                },
                                { shouldFocus: true }
                              )
                            }
                            onValid={() => clearErrors(name('income'))}
                            prefix="¥"
                            placeholder="0"
                            className="w-[110px] text-left"
                            style={incomeHighlightStyle}
                          />
                        </Form.Item>
                      )}
                    />
                    {/* 税区分 */}
                    <Controller
                      name={name('incomeTaxCategoryId')}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <TaxCategorySelect
                            tenantKey={tenantKey}
                            value={field.value ?? undefined}
                            onChange={(value) => field.onChange(value ?? null)}
                            placeholder={LABELS.TAX_CATEGORY}
                            className="!w-[110px] !min-w-0"
                          />
                        </Form.Item>
                      )}
                    />
                    {/* 内税 (VOC_PJ-844): === true のみ内税、OFF/未設定は外税 */}
                    <Controller
                      name={name('incomeIsTaxInclusive')}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value === true}
                          onChange={(e) => field.onChange(e.target.checked)}
                        >
                          {LABELS.TAX_INCLUSIVE}
                        </Checkbox>
                      )}
                    />
                    {/* 端数処理 (VOC_PJ-888 #3917): 税区分・内税の隣。null=カスケード fallback */}
                    <Controller
                      name={name('incomeRoundType')}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          htmlFor={field.name}
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <Select
                            options={ROUND_TYPE_OPTIONS}
                            value={field.value ?? undefined}
                            onChange={(value) => field.onChange(value ?? null)}
                            placeholder={LABELS.ROUNDING_UP_DOWN}
                            className="!w-[110px] !min-w-0"
                          />
                        </Form.Item>
                      )}
                    />
                    {/* 自動計算 */}
                    <Button
                      type="secondary"
                      icon={<FaCheckCircle />}
                      loading={isEstimating}
                      disabled={!isAutoCalcButtonEnabled}
                      onClick={handleAutoCalculate}
                      htmlType="button"
                    >
                      {ACTIONS.CALCULATE_AUTOMATICALLY}
                    </Button>
                    {/* 契約名 / 該当する契約が見つかりません */}
                    <div className="align-left">{renderContractNameCell()}</div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-4">
                    <NumberFormatter value={sourceOrder?.income ?? null} />
                    <span>{incomeTaxCategoryName ?? EMPTY_LABEL}</span>
                  </div>
                )}
              </BasicTd>
            </BasicTr>

            {/*
              VOC_PJ-29,431 #1857: 請求側付帯費用セクションを請求運賃の直下に配置 (FIX 仕様書 §B-1)。
              VOC_PJ-855 #3778: 並び順は「運賃 → 付帯費用 → 計上日」。請求計上日を付帯費用の
              直下に移動し、運賃行・付帯費用行を border-b-0 にして 運賃 → 付帯費用 → 計上日 を
              一塊表示にする。
            */}
            <BasicTr className="border-b-0">
              <BasicTd colSpan={4} className="!pb-0 !pt-0">
                <ShipmentOrderAdditionalsSection
                  fieldArrayName={name('additionalCharges') as FieldArrayName}
                  title={LABELS.SHIPMENT_ORDER_ADDITIONAL_CHARGES}
                  totalLabel={LABELS.ADDITIONAL_CHARGES_AMOUNT_TOTAL}
                  isEditing={canEditFields}
                  tenantKey={tenantKey}
                  className="!mt-0"
                />
              </BasicTd>
            </BasicTr>

            {/* VOC_PJ-855 #3778: 請求計上日（請求側付帯費用の直下）。発送日/着日 変更時に自動カスケード計算 */}
            <BasicTr>
              <BasicTh className="!pt-2 align-top">
                {LABELS.INCOME_RECORDING_DATE}
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd className="!pt-2 align-top" colSpan={3}>
                {canEditIncomeRecordingDate ? (
                  <Controller
                    control={control}
                    name={name('incomeRecordingDate')}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        htmlFor={field.name}
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <NormalizedDatePicker
                          id={field.name}
                          allowClear={false}
                          placeholder={STATUSES.UNSELECTED}
                          value={safeParseISO(field.value)}
                          onInvalidText={(msg) => {
                            setValue(name('incomeRecordingDate'), '', {
                              shouldValidate: false,
                              shouldDirty: true
                            })
                            setError(
                              name('incomeRecordingDate'),
                              { type: 'manual', message: msg },
                              { shouldFocus: true }
                            )
                          }}
                          onValidText={() =>
                            clearErrors(name('incomeRecordingDate'))
                          }
                          onChange={(date) => field.onChange(date)}
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  <DateFormatter
                    value={sourceOrder?.incomeRecordingDate}
                    dateFormat="yyyy/MM/dd"
                  />
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">{LABELS.TRUCK_MODEL}</BasicTh>
              <BasicTd className="align-top">
                {canEditFields ? (
                  <Controller
                    name={name('truckModelId')}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        htmlFor={field.name}
                        className="m-0"
                      >
                        <TruckModelSelect
                          {...field}
                          tenantKey={tenantKey}
                          id={field.name}
                          allowClear
                          dcId={sourceOrder?.group?.dcId}
                          onChange={(value) => {
                            field.onChange(value)
                            handleAutoCalculateIncomeValueChange()
                          }}
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  (sourceOrder?.truckModel?.name ?? '-')
                )}
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh className="align-top">{LABELS.PACKAGE_CATEGORY}</BasicTh>
              <BasicTd className="align-top">
                {canEditFields ? (
                  <Controller
                    name={name('packageCategoryId')}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Form.Item
                        validateStatus={error?.message ? 'error' : undefined}
                        help={error?.message}
                        className="m-0"
                      >
                        <PackageCategorySelect
                          {...field}
                          tenantKey={tenantKey}
                          id={field.name}
                          allowClear
                        />
                      </Form.Item>
                    )}
                  />
                ) : (
                  (sourceOrder?.group?.packageCategory?.name ?? '-')
                )}
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
        <ItemRows
          namePrefix={namePrefix}
          mode={mode}
          canEditFields={canEditFields}
          setNumericRef={setNumericRef}
          onWeightChange={handleAutoCalculateIncomeValueChange}
        />
        {/*
          VOC_PJ-29,431 #1857 / #3541 指摘-8: 請求側付帯費用 (対 荷主) は 請求運賃 BasicTr の
          直下に配置済 (FIX 仕様書 §B-1)。支払側 (払い側) は矢羽根 (DispatchDetail) 単位へ
          移行したため、配車前 (矢羽根未存在) の本フォームでは表示しない。
        */}
      </div>
    </>
  )
}
